import React, { useState, useEffect } from 'react';
import {
  Calendar, Clock, CheckCircle, AlertCircle, XCircle, X,
  Video, MapPin, Plane, ChevronDown, ChevronRight, Eye,
  FileText, Phone, Search, Filter, PawPrint, User, Mail
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SESSION_LABELS = {
  puppy_consultation: 'Puppy Consultation',
  follow_up: 'Follow-Up Session',
  one_on_one: '1-on-1 Training',
  behavior_assessment: 'Behavior Assessment',
  day_rate: 'Day Rate',
  two_day_intensive: 'Two-Day Intensive',
  behavior_assessment_travel: 'BA (Travel)',
};

const MODE_LABELS = {
  virtual: 'Virtual',
  local: 'In Person, Local',
  travel: 'In Person, Travel',
};

const MODE_ICONS = { virtual: Video, local: MapPin, travel: Plane };

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle },
  confirmed: { label: 'Confirmed', bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  completed: { label: 'Completed', bg: 'bg-blue-100', text: 'text-blue-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', bg: 'bg-gray-100', text: 'text-gray-500', icon: XCircle },
};

const INTAKE_STATUS_CONFIG = {
  new: { label: 'New', bg: 'bg-blue-100', text: 'text-blue-700' },
  reviewed: { label: 'Reviewed', bg: 'bg-amber-100', text: 'text-amber-700' },
  scheduled: { label: 'Scheduled', bg: 'bg-green-100', text: 'text-green-700' },
  declined: { label: 'Declined', bg: 'bg-gray-100', text: 'text-gray-500' },
};

function IntakeDetailModal({ form, open, onClose, onStatusChange }) {
  if (!form) return null;

  const status = INTAKE_STATUS_CONFIG[form.status] || INTAKE_STATUS_CONFIG.new;

  const fields = [
    { label: 'Dog', value: `${form.dog_name} (${form.dog_breed}), ${form.dog_age_months} months${form.dog_gender ? ', ' + form.dog_gender : ''}` },
    { label: 'Household', value: form.household_members },
    { label: 'Other Pets', value: form.other_pets },
    { label: 'Behaviors', value: form.specific_behaviors, highlight: true },
    { label: 'Duration', value: form.behavior_duration },
    { label: 'Prior Training', value: form.prior_training },
    { label: 'What Tried', value: form.what_tried },
    { label: 'Medical', value: form.medical_notes },
    { label: 'Goals', value: form.goals },
    { label: 'Referral', value: form.referral_source },
    { label: 'Quiz Done', value: form.quiz_completed ? 'Yes' : 'No' },
    { label: 'Preferred Call', value: form.preferred_contact_time },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-edupaws-navy flex items-center gap-2">
            <FileText className="w-5 h-5 text-edupaws-sage" />
            Intake Form: {form.owner_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Contact */}
          <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-body">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-bold text-edupaws-navy">{form.owner_name}</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-body text-gray-500">
                <Mail className="w-3.5 h-3.5" /> {form.email}
              </div>
              <div className="flex items-center gap-2 text-sm font-body text-gray-500">
                <Phone className="w-3.5 h-3.5" /> {form.phone}
              </div>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-body font-bold ${status.bg} ${status.text}`}>
              {status.label}
            </span>
          </div>

          {/* Details */}
          <div className="space-y-3">
            {fields.filter(f => f.value).map((field, i) => (
              <div key={i} className={`${field.highlight ? 'bg-amber-50 border border-amber-100' : 'bg-white border border-gray-100'} rounded-lg p-3`}>
                <p className="text-xs font-body font-bold text-gray-400 uppercase tracking-wider mb-1">{field.label}</p>
                <p className="text-sm font-body text-gray-700 whitespace-pre-wrap">{field.value}</p>
              </div>
            ))}
          </div>

          {/* Status Actions */}
          <div className="flex gap-2 pt-2">
            {['new', 'reviewed', 'scheduled', 'declined'].map(s => (
              <button
                key={s}
                data-testid={`intake-status-${s}`}
                onClick={() => onStatusChange(form.id, s)}
                disabled={form.status === s}
                className={`px-4 py-2 rounded-lg text-xs font-body font-bold transition-colors ${
                  form.status === s
                    ? `${INTAKE_STATUS_CONFIG[s].bg} ${INTAKE_STATUS_CONFIG[s].text}`
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {INTAKE_STATUS_CONFIG[s].label}
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [intakeForms, setIntakeForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState('sessions');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIntake, setSelectedIntake] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, intakeRes] = await Promise.all([
        axios.get(`${API}/admin/sessions`),
        axios.get(`${API}/admin/intake-forms`),
      ]);
      setSessions(sessionsRes.data);
      setIntakeForms(intakeRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (sessionId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/sessions/${sessionId}?status=${newStatus}`);
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, status: newStatus } : s));
      if (newStatus === 'confirmed') {
        toast.success('Session confirmed. Confirmation email sent to client.');
      } else {
        toast.success(`Session marked as ${newStatus}`);
      }
    } catch (err) {
      toast.error('Failed to update session');
    }
  };

  const handleIntakeStatusChange = async (formId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/intake-forms/${formId}?status=${newStatus}`);
      setIntakeForms(prev => prev.map(f => f.id === formId ? { ...f, status: newStatus } : f));
      setSelectedIntake(prev => prev?.id === formId ? { ...prev, status: newStatus } : prev);
      toast.success(`Intake form marked as ${newStatus}`);
    } catch (err) {
      toast.error('Failed to update intake form');
    }
  };

  const filteredSessions = sessions.filter(s => {
    const matchSearch = !search || s.owner_name?.toLowerCase().includes(search.toLowerCase()) || s.dog_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || s.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const filteredIntake = intakeForms.filter(f => {
    const matchSearch = !search || f.owner_name?.toLowerCase().includes(search.toLowerCase()) || f.dog_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || f.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const pendingCount = sessions.filter(s => s.status === 'pending').length;
  const newIntakeCount = intakeForms.filter(f => f.status === 'new').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-edupaws-sage border-t-transparent" />
      </div>
    );
  }

  return (
    <div data-testid="sessions-admin-tab" className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-body font-medium text-gray-400 uppercase">Total Sessions</p>
          <p className="text-2xl font-heading font-bold text-gray-800 mt-1">{sessions.length}</p>
        </div>
        <div className={`rounded-xl border p-4 ${pendingCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-gray-200'}`}>
          <p className={`text-xs font-body font-medium uppercase ${pendingCount > 0 ? 'text-amber-600' : 'text-gray-400'}`}>Pending</p>
          <p className={`text-2xl font-heading font-bold mt-1 ${pendingCount > 0 ? 'text-amber-700' : 'text-gray-800'}`}>{pendingCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-body font-medium text-gray-400 uppercase">Intake Forms</p>
          <p className="text-2xl font-heading font-bold text-gray-800 mt-1">{intakeForms.length}</p>
        </div>
        <div className={`rounded-xl border p-4 ${newIntakeCount > 0 ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
          <p className={`text-xs font-body font-medium uppercase ${newIntakeCount > 0 ? 'text-blue-600' : 'text-gray-400'}`}>New Intake</p>
          <p className={`text-2xl font-heading font-bold mt-1 ${newIntakeCount > 0 ? 'text-blue-700' : 'text-gray-800'}`}>{newIntakeCount}</p>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-6 border-b border-gray-200">
        <button
          data-testid="subtab-sessions"
          onClick={() => { setSubTab('sessions'); setStatusFilter('all'); }}
          className={`pb-3 px-1 text-sm font-body font-bold border-b-2 transition-colors ${
            subTab === 'sessions' ? 'border-edupaws-sage text-edupaws-navy' : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Session Requests {pendingCount > 0 && <span className="ml-1 bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full text-xs">{pendingCount}</span>}
        </button>
        <button
          data-testid="subtab-intake"
          onClick={() => { setSubTab('intake'); setStatusFilter('all'); }}
          className={`pb-3 px-1 text-sm font-body font-bold border-b-2 transition-colors ${
            subTab === 'intake' ? 'border-edupaws-sage text-edupaws-navy' : 'border-transparent text-gray-400 hover:text-gray-600'
          }`}
        >
          Intake Forms {newIntakeCount > 0 && <span className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-xs">{newIntakeCount}</span>}
        </button>
      </div>

      {/* Search + Filter */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="sessions-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name..."
            className="pl-10 h-10 bg-white border-gray-200 rounded-lg font-body text-sm"
          />
        </div>
        <select
          data-testid="sessions-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm font-body"
        >
          <option value="all">All Status</option>
          {subTab === 'sessions' ? (
            <>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </>
          ) : (
            <>
              <option value="new">New</option>
              <option value="reviewed">Reviewed</option>
              <option value="scheduled">Scheduled</option>
              <option value="declined">Declined</option>
            </>
          )}
        </select>
      </div>

      {/* Sessions List */}
      {subTab === 'sessions' && (
        <div className="space-y-3">
          {filteredSessions.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="font-body text-gray-400 text-sm">No session requests yet</p>
            </div>
          ) : (
            filteredSessions.map(session => {
              const statusConf = STATUS_CONFIG[session.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConf.icon;
              const ModeIcon = MODE_ICONS[session.session_mode] || Video;
              return (
                <div key={session.id} data-testid={`admin-session-${session.id}`} className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-edupaws-navy/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <ModeIcon className="w-5 h-5 text-edupaws-navy" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-body font-bold text-sm text-edupaws-navy">{session.owner_name}</h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-body font-bold ${statusConf.bg} ${statusConf.text}`}>
                            <StatusIcon className="w-3 h-3" /> {statusConf.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 font-body mt-0.5">
                          {SESSION_LABELS[session.session_type] || session.session_type}
                          {session.session_mode ? ` / ${MODE_LABELS[session.session_mode] || session.session_mode}` : ''}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 font-body">
                          <span className="flex items-center gap-1"><PawPrint className="w-3 h-3" /> {session.dog_name}</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {session.preferred_date}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {session.preferred_time}</span>
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {session.owner_email}</span>
                        </div>
                        {session.notes && (
                          <p className="mt-2 text-xs text-gray-500 font-body bg-gray-50 p-2 rounded-lg">{session.notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Status Actions */}
                  <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
                    {['pending', 'confirmed', 'completed', 'cancelled'].map(s => (
                      <button
                        key={s}
                        data-testid={`session-action-${s}-${session.id}`}
                        onClick={() => handleStatusChange(session.id, s)}
                        disabled={session.status === s}
                        className={`px-3 py-1.5 rounded-lg text-xs font-body font-bold transition-colors ${
                          session.status === s
                            ? `${STATUS_CONFIG[s].bg} ${STATUS_CONFIG[s].text}`
                            : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                        }`}
                      >
                        {STATUS_CONFIG[s].label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Intake Forms List */}
      {subTab === 'intake' && (
        <div className="space-y-3">
          {filteredIntake.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="font-body text-gray-400 text-sm">No intake forms submitted yet</p>
              <p className="font-body text-xs text-gray-300 mt-1">Share the intake form link with prospects</p>
            </div>
          ) : (
            filteredIntake.map(form => {
              const statusConf = INTAKE_STATUS_CONFIG[form.status] || INTAKE_STATUS_CONFIG.new;
              return (
                <div key={form.id} data-testid={`intake-row-${form.id}`} className="bg-white rounded-xl border border-gray-200 p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-edupaws-sage/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-edupaws-sage" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-body font-bold text-sm text-edupaws-navy">{form.owner_name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-body font-bold ${statusConf.bg} ${statusConf.text}`}>{statusConf.label}</span>
                          {!form.quiz_completed && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-body font-bold bg-red-100 text-red-600">Quiz not done</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 font-body mt-0.5">
                          Dog: {form.dog_name} ({form.dog_breed}), {form.dog_age_months} months
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400 font-body">
                          <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {form.email}</span>
                          <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {form.phone}</span>
                          <span>{new Date(form.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="mt-2 text-xs text-gray-600 font-body bg-amber-50 p-2 rounded-lg line-clamp-2">
                          <strong>Behaviors:</strong> {form.specific_behaviors}
                        </p>
                      </div>
                    </div>
                    <button
                      data-testid={`view-intake-${form.id}`}
                      onClick={() => setSelectedIntake(form)}
                      className="p-2 rounded-lg text-gray-400 hover:text-edupaws-navy hover:bg-gray-100 transition-colors flex-shrink-0"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Intake Form Detail Modal */}
      <IntakeDetailModal
        form={selectedIntake}
        open={!!selectedIntake}
        onClose={() => setSelectedIntake(null)}
        onStatusChange={handleIntakeStatusChange}
      />
    </div>
  );
}
